import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, IconButton } from '@c2fo/liquidity';
import { ArrowUpRightFromSquareIcon, PenIcon } from '@c2fo/liquidity/icons';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import IncludeEligibleInvoicesModal from '@/enterprise/components/IncludeEligibleInvoicesModal';
import useAggregateTakerMarketStats from '@/enterprise/data/useAggregateTakerMarketStats';
import { TakerMarketGroupType } from '@/enterprise/data/useGroupByPricingType';
import useTakerMarketsGroups from '@/enterprise/data/useTakerMarketsGroups';
import TabActionsDropdown from '@/enterprise/features/takerMarketTabs/components/TabActionsDropdown';
import { useMarketStatsStatus } from '@/lib/useMarketStatsStatus';
import { useReporting } from '@/reporting';
import IntercomDataTarget from '@/reporting/IntercomDataTarget';
import { IconButtonLink } from '@/shared/components/IconButtonLink';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import useLocaleFormat from '@/utils/useLocaleFormat';
import useRestrictions from '@/utils/useRestrictions';

interface TabContentProps {
  description: string;
  showActions: boolean;
  showDescription: boolean;
  showStats: boolean;
  tab: TakerMarketGroupType;
}

const TabContent = ({ description, showActions, showDescription, showStats, tab }: TabContentProps) => {
  const { t } = useTranslation();
  const { asCurrency } = useLocaleFormat();
  const { selectedCurrency } = useCurrencyContext();
  const { trackEnterpriseEvent } = useReporting();
  const takerMarketsGroups = useTakerMarketsGroups();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const data = takerMarketsGroups[tab];
  const takerMarketStats = useAggregateTakerMarketStats(data.takerMarkets);
  const takerMarketsWithRulesStats = useAggregateTakerMarketStats(data.takerMarketsWithRules);
  const takerMarketsWithoutRulesStats = useAggregateTakerMarketStats(data.takerMarketsWithoutRules);
  const takerMarketsToReinclude = data.takerMarketsWithoutRules.filter((tm) => tm.takerExcludedInvoiceAmount > 0);
  const { listenToMarketStats } = useMarketStatsStatus();
  const listeningForMarketStat = listenToMarketStats(
    data.takerMarkets.map(({ marketUuid, takerDivisionId, takerDivisionUuid, makerOrganizationUuid }) => ({
      marketUuid,
      takerId: takerDivisionId,
      takerDivisionUuid,
      makerOrganizationUuid,
    }))
  );
  const { getRestrictions } = useRestrictions();
  const { canEditInvoices } = getRestrictions(data.takerMarketsWithoutRules);
  const manualInvoiceExclusionsDisabled = takerMarketsToReinclude.length === 0 || !canEditInvoices;

  const handleOpenModal = () => {
    setOpenModal(true);
    trackEnterpriseEvent('invoice-include-banner::clicked', {
      tab,
      takerMarkets: takerMarketsToReinclude.map((tm) => ({
        takerId: tm.takerDivisionId,
        marketUuid: tm.marketUuid,
      })),
    });
  };

  const handleCloseModal = () => setOpenModal(false);

  const includedAmount = takerMarketStats.byCurrency[selectedCurrency]?.eligibleInvoiceAmount;

  const estimatedAmount =
    takerMarketStats.byCurrency[selectedCurrency]?.clearingInvoiceAmount -
    takerMarketStats.byCurrency[selectedCurrency]?.clearingEarn;

  return (
    <>
      <IncludeEligibleInvoicesModal
        onClose={handleCloseModal}
        open={openModal}
        tab={tab}
        takerMarkets={takerMarketsToReinclude}
      />
      <div className="flex-1 space-y-4">
        <div className="flex min-h-[48px] items-center justify-between gap-6">
          {showDescription && <div>{description}</div>}
          {showActions && <TabActionsDropdown tab={tab} />}
        </div>
        {showStats && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="flex items-center justify-between gap-4 rounded-lg border-[0.5px] border-stroke bg-canvas px-6 py-8">
              <div className="space-y-0.5">
                <div className={cn('font-serif text-xl font-bold', { 'blur-sm': listeningForMarketStat })}>
                  {asCurrency(includedAmount, selectedCurrency)}
                </div>
                <div className="text-sm font-medium text-text-secondary">{t('core.includedAmount')}</div>
              </div>
            </div>
            <div className="flex items-center justify-between gap-4 rounded-lg border-[0.5px] border-stroke bg-canvas px-6 py-8">
              <div className="space-y-0.5">
                <div className={cn('font-serif text-xl font-bold', { 'blur-sm': listeningForMarketStat })}>
                  {asCurrency(estimatedAmount, selectedCurrency)}
                </div>
                <div className="text-sm font-medium text-text-secondary">{t('core.estimatedEarlyPaymentAmount')}</div>
              </div>
            </div>
            {tab !== 'PREFERRED' && (
              <>
                <IntercomDataTarget
                  target="marketTableKpiManualExclusions"
                  className="flex items-center justify-between gap-4 rounded-lg border-[0.5px] border-stroke bg-canvas px-6 py-8"
                >
                  <div className="space-y-0.5">
                    <div className={cn('font-serif text-xl font-bold', { 'blur-sm': listeningForMarketStat })}>
                      {asCurrency(
                        takerMarketsWithoutRulesStats.byCurrency[selectedCurrency]?.takerExcludedInvoiceAmount,
                        selectedCurrency
                      )}
                    </div>
                    <div className="text-sm font-medium text-text-secondary">{t('core.manualInvoiceExclusions')}</div>
                  </div>
                  <div>
                    <IconButton
                      disabled={manualInvoiceExclusionsDisabled}
                      icon={PenIcon}
                      name={t('core.manualInvoiceExclusions')}
                      onClick={handleOpenModal}
                      variant="tertiary"
                    />
                  </div>
                </IntercomDataTarget>
                <IntercomDataTarget
                  target="marketTableKpiAutomatedExclusions"
                  className="flex items-center justify-between gap-4 rounded-lg border-[0.5px] border-stroke bg-canvas px-6 py-8"
                >
                  <div className="space-y-0.5">
                    <div className={cn('font-serif text-xl font-bold', { 'blur-sm': listeningForMarketStat })}>
                      {asCurrency(
                        takerMarketsWithRulesStats.byCurrency[selectedCurrency]?.takerExcludedInvoiceAmount,
                        selectedCurrency
                      )}
                    </div>
                    <div className="text-sm font-medium text-text-secondary">
                      {t('core.automatedInvoiceExclusions')}
                    </div>
                  </div>
                  <div>
                    <IconButtonLink
                      icon={ArrowUpRightFromSquareIcon}
                      name={t('core.automatedInvoiceExclusions')}
                      to={asEnterpriseLink('automation')}
                      variant="tertiary"
                    />
                  </div>
                </IntercomDataTarget>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};

const TabActions = ({ activeTab }: { activeTab: TakerMarketGroupType }) => {
  const { t } = useTranslation();
  const takerMarketsGroups = useTakerMarketsGroups();

  const hasPreferredMarkets = takerMarketsGroups['PREFERRED'].rollup.count > 0;
  const hasNameYourRateMarkets = takerMarketsGroups['NAME_YOUR_RATE'].rollup.count > 0;
  const hasBenchmarkMarkets = takerMarketsGroups['BENCHMARK'].rollup.count > 0;
  const hasFixedMarkets = takerMarketsGroups['FIXED'].rollup.count > 0;

  const tabConfig: Record<TakerMarketGroupType, TabContentProps> = {
    BENCHMARK: {
      description: t('takerMarketTabs.variableRateDescription'),
      showActions: hasBenchmarkMarkets,
      showDescription: true,
      showStats: true,
      tab: 'BENCHMARK',
    },
    FIXED: {
      description: t('takerMarketTabs.fixedRateDescription'),
      showActions: hasFixedMarkets,
      showDescription: true,
      showStats: true,
      tab: 'FIXED',
    },
    INACTIVE: {
      description: t('takerMarketTabs.inactiveMarketsDescription'),
      showActions: false,
      showDescription: true,
      showStats: false,
      tab: 'INACTIVE',
    },
    NAME_YOUR_RATE: {
      description: t('takerMarketTabs.nameYourRateDescription'),
      showActions: hasNameYourRateMarkets,
      showDescription: hasNameYourRateMarkets,
      showStats: true,
      tab: 'NAME_YOUR_RATE',
    },
    PREFERRED: {
      description: t('takerMarketTabs.preferredRateDescription'),
      showActions: false,
      showDescription: hasPreferredMarkets,
      showStats: hasPreferredMarkets,
      tab: 'PREFERRED',
    },
  };

  return (
    <div className="flex min-h-[80px] justify-between p-4 sm:flex-row sm:items-center sm:space-y-0">
      <TabContent {...tabConfig[activeTab]} />
    </div>
  );
};

export default TabActions;
